import { Table } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerTableActionButtons } from '../../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../../../components/custom_components/Text'
import { useAuth } from '../../../../context/auth-context'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../../shared/useDateTime'
import { ObjectiveDto, Currency } from '../../../../types'
import { isDefined } from '../../../../types.helper'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { useCurrency } from '../../../../context/currency-context'

interface ClosedTargetsProps {
  monetary?: boolean
  objectives: ObjectiveDto[]
  onObjectiveViewClick: (target: ObjectiveDto) => void
}

export const ClosedTargetsTable: FC<ClosedTargetsProps> = ({ monetary, objectives, onObjectiveViewClick }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const { formatCurrency, formatAmountAsPercentage, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { tableActionButtonsRowWidth } = tableActionButtons()

  const { currency: currentContractCurrency, currencyConversionData } = useCurrency()
  const columns = [
    {
      key: 'title',
      title: t(`appraisalMeeting.closedTargetsOverview.table.title.${monetary ? 'monetary' : 'development'}`),
      className: 'textAlign-left minWidth-column-xl',
      render: (objective: ObjectiveDto) => (
        <PerText fontWeight="bold" my="xxs">
          {objective.title}
        </PerText>
      ),
    },
    {
      key: 'initialTarget',
      title: t(`appraisalMeeting.closedTargetsOverview.table.target`),
      className: 'textAlign-right minWidth-column-m',
      render: (objective: ObjectiveDto) => {
        return (
          <PerFlexRow direction="column">
            {isDefined(objective.targetAmount) && isDefined(objective.currency) && (
              <PerText>{formatCurrency(objective.targetAmount ? objective.targetAmount : 0, objective.currency!)}</PerText>
            )}
            {isDefined(objective.targetAmount) && isDefined(objective.currency) && objective.currency !== currentContractCurrency && (
              <PerText fontSize="xs" mt="xxs">
                {calculateAndFormatNewTargetAmount(
                  objective.targetAmount!,
                  currentContractCurrency,
                  retrieveCurrencyConversionRate(objective.currency as Currency, currentContractCurrency, currencyConversionData),
                )}
              </PerText>
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'achievedMonetary',
      title: t(`appraisalMeeting.closedTargetsOverview.table.achievedMonetary`),
      className: 'textAlign-right minWidth-column-m',
      render: (objective: ObjectiveDto) => (
        <>
          {objective.achievedAmount && objective.targetAmount
            ? formatAmountAsPercentage(objective.achievedAmount / objective.targetAmount, false)
            : ''}
        </>
      ),
    },
    {
      key: 'achievedDevelopment',
      title: t(`appraisalMeeting.closedTargetsOverview.table.achievedDevelopment`),
      className: 'textAlign-left minWidth-column-xl',
      render: (objective: ObjectiveDto) => (
        <>
          {!!objective.keyResults.length
            ? formatAmountAsPercentage(objective.keyResults.filter((o) => o.completed).length / objective.keyResults.length, false)
            : ''}
        </>
      ),
    },
    {
      key: 'hiddenAmount',
      title: '',
      className: 'textAlign-right minWidth-column-l',
      render: () => <></>,
    },
    {
      key: 'amount',
      title: t(`appraisalMeeting.closedTargetsOverview.table.amount`),
      className: 'textAlign-right minWidth-column-l',
      render: (objective: ObjectiveDto) => {
        return (
          <PerFlexRow direction="column">
            {isDefined(objective.achievedAmount) && isDefined(objective.currency) && (
              <PerText>{formatCurrency(objective.achievedAmount ? objective.achievedAmount : 0, objective.currency!)}</PerText>
            )}
            {isDefined(objective.achievedAmount) && isDefined(objective.currency) && objective.currency !== currentContractCurrency && (
              <PerText fontSize="xs" mt="xxs">
                {calculateAndFormatNewTargetAmount(
                  objective.achievedAmount!,
                  currentContractCurrency,
                  retrieveCurrencyConversionRate(objective.currency as Currency, currentContractCurrency, currencyConversionData),
                )}
              </PerText>
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'closedDate',
      title: t(`appraisalMeeting.closedTargetsOverview.table.closedDate`),
      className: 'textAlign-left minWidth-column-m',
      render: (objective: ObjectiveDto) => <>{objective.closedDate && formatDateShort(objective.closedDate)}</>,
    },
    {
      key: 'actions',
      title: t(`appraisalMeeting.closedTargetsOverview.table.actions`),
      width: tableActionButtonsRowWidth(1),
      className: 'textAlign-left',
      render: (objective: ObjectiveDto) => (
        <PerTableActionButtons
          buttons={[
            {
              icon: 'view_fill',
              color: 'primary',
              tooltip: t('tooltip.view'),
              onClick: () => onObjectiveViewClick(objective),
            },
          ]}
        />
      ),
    },
  ]

  const filterColumns = (columns: { key: string }[]) =>
    monetary
      ? columns.filter((col) => !['achievedDevelopment', 'hiddenAmount'].some((i) => i === col.key)) // filter out dev columns
      : columns.filter((col) => !['achievedMonetary', 'amount'].some((i) => i === col.key)) // filter out monetary columns

  return <Table dataSource={objectives} rowKey="globalId" columns={filterColumns(columns)} pagination={false} />
}
